<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /** header = tengah app bar; profile = di dalam dropdown */
    variant?: 'header' | 'profile';
    compact?: boolean;
  }>(),
  { variant: 'header', compact: false }
);

const { clockLabel, sessionLabel, urgent, startWatchers, countdown } = useIdleSession();
const { t } = useI18n();
const { mode: deviceMode } = useDevicePreview();

/** Compact: tanggal pendek */
const compactDate = computed(() => {
  const full = clockLabel.value;
  // "Sabtu - 08/08/2026, 11:17 AM" → "08/08 · 11:17 AM"
  const m = full.match(/(\d{2})\/(\d{2})\/(\d{4}),\s*(.+)$/);
  if (m) return `${m[1]}/${m[2]} · ${m[4]}`;
  return full;
});
/** HP: hanya hitung mundur di baris bawah; lain: "Sesi {time}" */
const compactSesi = computed(() =>
  deviceMode.value === 'hp'
    ? countdown.value
    : t('auth.sessionShort', { time: countdown.value })
);

onMounted(() => {
  startWatchers();
});
</script>

<template>
  <div
    class="session-clock"
    :class="[
      `session-clock--${props.variant}`,
      {
        'session-clock--urgent': urgent,
        'session-clock--compact': props.compact,
        'session-clock--stack': props.compact && props.variant === 'header',
      },
    ]"
    :title="`${clockLabel} · ${sessionLabel}`"
  >
    <template v-if="props.compact && props.variant === 'header'">
      <div class="session-clock__date">{{ compactDate }}</div>
      <div class="session-clock__sesi">{{ compactSesi }}</div>
    </template>
    <template v-else>
      <div class="session-clock__date">{{ clockLabel }}</div>
      <div class="session-clock__sesi">{{ sessionLabel }}</div>
    </template>
  </div>
</template>

<style scoped>
.session-clock {
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.15;
  user-select: none;
  pointer-events: none;
  min-width: 0;
  max-width: 100%;
}
.session-clock--header {
  align-items: center;
  text-align: center;
  padding: 2px 8px;
}
/* Compact header: tgl/jam atas, sesi bawah (hemat lebar) */
.session-clock--header.session-clock--compact,
.session-clock--header.session-clock--stack {
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  gap: 1px;
  padding: 0;
}
.session-clock--profile {
  align-items: flex-start;
  text-align: left;
  flex: 1;
  min-width: 0;
}
.session-clock--profile .session-clock__date {
  font-size: 12px;
  font-weight: 800;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  max-width: none;
  overflow-wrap: anywhere;
}
.session-clock--profile .session-clock__sesi {
  font-size: 11px;
  font-weight: 700;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
:global(html.jgi-device-hp .session-clock--profile .session-clock__date){
  font-size: 11px;
}
:global(html.jgi-device-hp .session-clock--profile .session-clock__sesi){
  font-size: 10px;
}
.session-clock__date {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.session-clock__sesi {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #64748b;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.session-clock--header .session-clock__date {
  font-size: 13px;
}
.session-clock--header .session-clock__sesi {
  font-size: 11px;
}
.session-clock--compact .session-clock__date,
.session-clock--header.session-clock--compact .session-clock__date {
  font-size: 10.5px;
  line-height: 1.15;
}
.session-clock--compact .session-clock__sesi,
.session-clock--header.session-clock--compact .session-clock__sesi {
  margin-top: 0;
  font-size: 9.5px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0.02em;
}
:global(html.jgi-device-hp .session-clock--header.session-clock--compact .session-clock__date){
  font-size: 10px;
  font-weight: 800;
}
:global(html.jgi-device-hp .session-clock--header.session-clock--compact .session-clock__sesi){
  font-size: 9px;
  font-weight: 700;
  opacity: 0.92;
}
.session-clock--urgent .session-clock__sesi {
  color: #ea580c;
  font-weight: 800;
}

:global(html.jgi-dark .session-clock__date){
  color: #f8fafc;
}
:global(html.jgi-dark .session-clock__sesi){
  color: #94a3b8;
}
:global(html.jgi-dark .session-clock--urgent .session-clock__sesi){
  color: #fb923c;
}
</style>
